import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Phone, Clock3 } from "lucide-react";
import { COMPANY, BLOG_POSTS, SERVICE_PAGES, SERVICES } from "@/lib/site-data";
import LogoEmblem from "@/components/LogoEmblem";
import { useSeo, orgSchema } from "@/lib/useSeo";

const ease = [0.22, 1, 0.36, 1];

export default function BlogPost() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);
  const relatedService = post ? SERVICE_PAGES.find((s) => s.slug === post.service) : null;
  const relatedTitle = relatedService ? SERVICES.find((sv) => sv.id === relatedService.serviceId)?.title : null;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useSeo({
    title: post ? `${post.title} — NB Couverture Bordeaux` : undefined,
    description: post?.meta,
    canonical: post ? `/conseils/${post.slug}` : undefined,
    schemas: post
      ? [
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Accueil", item: "https://nb-couverture-bordeaux.fr/" },
              { "@type": "ListItem", position: 2, name: "Conseils", item: "https://nb-couverture-bordeaux.fr/conseils" },
              { "@type": "ListItem", position: 3, name: post.title, item: `https://nb-couverture-bordeaux.fr/conseils/${post.slug}` },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.title,
            description: post.meta,
            articleSection: post.cat,
            inLanguage: "fr-FR",
            author: orgSchema,
            publisher: { "@type": "Organization", name: "NB Couverture" },
          },
        ]
      : [],
  });

  if (!post) return <Navigate to="/conseils" replace />;
  const others = BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-[#0D0F12] text-slate-200 antialiased">
      <header className="sticky top-0 z-40 border-b border-white/5 bg-[#0D0F12]/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-3" data-testid="post-logo">
            <LogoEmblem size={40} />
            <span className="font-serif text-sm font-semibold text-white">NB Couverture</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/conseils"
              data-testid="post-back-blog"
              className="hidden items-center gap-2 text-xs font-semibold text-slate-300 transition-colors hover:text-terra sm:inline-flex"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Tous les conseils
            </Link>
            <a
              href={COMPANY.phoneHref}
              className="inline-flex items-center gap-2 rounded-full bg-terra px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-terra-hover"
            >
              <Phone className="h-3.5 w-3.5" />
              {COMPANY.phone}
            </a>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-16 md:py-24">
        <motion.article initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease }}>
          <div className="flex flex-wrap items-center gap-4 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
            <span className="text-terra">{post.cat}</span>
            <span>{post.date}</span>
            <span className="flex items-center gap-1.5">
              <Clock3 className="h-3 w-3" />
              {post.read}
            </span>
          </div>
          <h1 className="mt-6 font-serif text-3xl font-semibold leading-tight text-white md:text-5xl">{post.title}</h1>
          <p data-testid="post-intro" className="mt-6 border-l-2 border-terra pl-5 text-base leading-relaxed text-slate-300 md:text-lg">
            {post.intro}
          </p>

          {post.sections.map((s) => (
            <section key={s.h} className="mt-12">
              <h2 className="font-serif text-2xl font-semibold text-white">{s.h}</h2>
              {s.ps.map((p, i) => (
                <p key={i} className="mt-4 text-sm leading-relaxed text-slate-300 md:text-base">
                  {p}
                </p>
              ))}
            </section>
          ))}

          <div className="mt-14 border border-terra/25 bg-terra/5 p-7 md:p-9">
            <h2 className="font-serif text-xl font-semibold text-white md:text-2xl">
              Un doute sur votre toit ? Le diagnostic est gratuit.
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-300">
              Nous montons, nous regardons, nous vous disons franchement ce qu'il en est — avec photos à l'appui.
            </p>
            {relatedService && relatedTitle && (
              <p className="mt-4 text-sm">
                <Link
                  to={`/services/${relatedService.slug}`}
                  data-testid="post-related-service"
                  className="font-semibold text-terra underline-offset-4 transition-colors hover:text-terra-hover hover:underline"
                >
                  La prestation concernée : {relatedTitle} →
                </Link>
              </p>
            )}
            <div className="mt-6 flex flex-wrap gap-4">
              <a
                href="/#devis"
                data-testid="post-devis-cta"
                className="group inline-flex items-center gap-3 rounded-full bg-terra px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-terra-hover"
              >
                Demander un devis gratuit
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <a
                href={COMPANY.phoneHref}
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3.5 text-sm font-semibold text-slate-200 transition-colors hover:border-terra/60 hover:text-white"
              >
                <Phone className="h-4 w-4 text-terra" />
                {COMPANY.phone}
              </a>
            </div>
          </div>
        </motion.article>

        <section className="mt-16 border-t border-white/5 pt-10">
          <h2 className="font-serif text-xl font-semibold text-white">À lire aussi</h2>
          <div className="mt-6 grid gap-4">
            {others.map((p) => (
              <Link
                key={p.slug}
                to={`/conseils/${p.slug}`}
                data-testid={`post-related-${p.slug}`}
                className="group flex items-center justify-between gap-4 border border-white/5 bg-[#14171D] p-5 transition-colors duration-300 hover:border-terra/40"
              >
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-terra">{p.cat}</p>
                  <p className="mt-1.5 font-serif text-base text-slate-100 transition-colors group-hover:text-terra">{p.title}</p>
                </div>
                <ArrowRight className="h-4 w-4 shrink-0 text-slate-500 transition-transform duration-300 group-hover:translate-x-1 group-hover:text-terra" />
              </Link>
            ))}
          </div>
        </section>
      </main>

      <footer className="border-t border-white/5 py-8">
        <p className="mx-auto max-w-6xl px-6 text-xs text-slate-500">
          {COMPANY.name} — {COMPANY.address} · {COMPANY.phone} · Garantie décennale ·{" "}
          <Link to="/" className="text-slate-400 underline-offset-4 hover:text-terra hover:underline">
            Accueil
          </Link>
        </p>
      </footer>
    </div>
  );
}
